import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import axios from "axios";
import { fetchCurrentUser, fetchAdminMe, logout as apiLogout, adminLogout as apiAdminLogout, API } from "./auth";

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [admin, setAdmin] = useState(null);
  const [loading, setLoading] = useState(true);
  const [unreadCount, setUnreadCount] = useState(0);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      if (typeof window !== "undefined" && window.location?.hash?.includes("session_id=")) {
        setLoading(false);
        return;
      }
      const [u, a] = await Promise.all([fetchCurrentUser(), fetchAdminMe()]);
      setUser(u);
      setAdmin(a);
    } finally {
      setLoading(false);
    }
  }, []);

  const refreshUnread = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/my/unread-count`, { withCredentials: true });
      setUnreadCount(data?.unread || 0);
    } catch (err) {
      if (err?.response?.status !== 401) console.warn("[Auth] unread-count failed:", err?.message || err);
    }
  }, []);

  const markNotificationsSeen = useCallback(async () => {
    try {
      await axios.post(`${API}/my/notifications/seen`, {}, { withCredentials: true });
      setUnreadCount(0);
    } catch (err) {
      console.warn("[Auth] notifications/seen failed:", err?.message || err);
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  // Poll unread count every 45s while a customer is logged in
  useEffect(() => {
    if (!user) { setUnreadCount(0); return; }
    refreshUnread();
    const id = setInterval(refreshUnread, 45000);
    return () => clearInterval(id);
  }, [user, refreshUnread]);

  const logout = async () => {
    try { await apiLogout(); } catch (e) { console.warn("Customer logout failed:", e); }
    setUser(null);
    setUnreadCount(0);
  };

  const adminLogout = async () => {
    try { await apiAdminLogout(); } catch (e) { console.warn("Admin logout failed:", e); }
    setAdmin(null);
  };

  return (
    <AuthContext.Provider value={{ user, admin, loading, unreadCount, refresh, refreshUnread, markNotificationsSeen, logout, adminLogout, setUser, setAdmin }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
